import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { organizationAPI } from '../services/api';
import { Store as StoreType } from '../types';
import { X, Check, CheckCheck, RefreshCw, Clock, History } from 'lucide-react';

interface Props {
  store: StoreType | null;
  isOpen: boolean;
  onClose: () => void;
  onChanged?: () => void;
}

// Tone by change kind: added/sync → green, removed → red, updated → blue.
const typeTone = (type: string) => {
  if (!type) return 'bg-surface-3 dark:bg-surface-3/20 text-t3';
  if (type.endsWith('_added') || type === 'bulk_sync') return 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400';
  if (type.endsWith('_removed')) return 'bg-red-500/15 text-red-600 dark:text-red-400';
  if (type.endsWith('_updated')) return 'bg-blue-500/15 text-blue-600 dark:text-blue-400';
  return 'bg-surface-3 dark:bg-surface-3/20 text-t3';
};

const CatalogChangesModal: React.FC<Props> = ({ store, isOpen, onClose, onChanged }) => {
  const { t } = useTranslation(['admin', 'common']);
  const [pending, setPending] = useState<any[]>([]);
  const [history, setHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<number | 'all' | null>(null);

  const typeLabel = (type: string) => t(`admin:changeTypes.${type}`, { defaultValue: type });
  const fmtDate = (s: string) => s ? new Date(s).toLocaleString('el-GR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';
  const statusLabel = (st: string) => (({ approved: 'Εγκρίθηκε', auto_applied: 'Αυτόματα', rejected: 'Απορρίφθηκε' } as any)[st] || st);

  const load = useCallback(async () => {
    if (!store?.id) return;
    setLoading(true);
    try {
      const [p, h] = await Promise.all([
        organizationAPI.getPendingSyncRequests(store.id),
        organizationAPI.getSyncHistory(store.id),
      ]);
      setPending(p.requests || []);
      setHistory(h.history || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [store?.id]);

  useEffect(() => { if (isOpen) load(); }, [isOpen, load]);

  const accept = async (id: number) => {
    setBusy(id);
    try { await organizationAPI.approveSyncRequest(id); await load(); onChanged?.(); }
    catch (e: any) { alert(e?.response?.data?.error || 'Σφάλμα'); }
    finally { setBusy(null); }
  };

  const acceptAll = async () => {
    if (!store?.id) return;
    if (!window.confirm(t('admin:approveAllConfirm', { count: pending.length, defaultValue: `Αποδοχή ${pending.length} αλλαγών;` }))) return;
    setBusy('all');
    try { await organizationAPI.approveAllSyncRequests(store.id); await load(); onChanged?.(); }
    catch (e: any) { alert(e?.response?.data?.error || 'Σφάλμα'); }
    finally { setBusy(null); }
  };

  if (!isOpen) return null;

  return (
    <div className="cashier-overlay fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="cashier-dialog bg-surface-2 dark:bg-surface-1 rounded-2xl border border-line w-full max-w-2xl shadow-2xl max-h-[88vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="cashier-dialog-header flex items-center justify-between px-6 py-4 border-b border-line">
          <div className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            <h3 className="text-lg font-bold text-t1">Αλλαγές καταλόγου</h3>
            {pending.length > 0 && <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-red-500/20 text-red-600 dark:text-red-400">{pending.length}</span>}
          </div>
          <button onClick={onClose} className="p-1 text-t3 hover:text-t1"><X className="h-5 w-5" /></button>
        </div>

        <div className="p-6 space-y-6 overflow-y-auto">
          {/* Pending */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-t1 flex items-center gap-2"><Clock className="h-4 w-4 text-amber-700 dark:text-amber-400" /> Σε εκκρεμότητα ({pending.length})</h4>
              {pending.length > 1 && (
                <button onClick={acceptAll} disabled={busy === 'all'}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg disabled:opacity-50">
                  <CheckCheck className="h-4 w-4" /> Αποδοχή όλων
                </button>
              )}
            </div>
            {loading ? (
              <div className="text-center text-t3 py-6">Φόρτωση…</div>
            ) : pending.length === 0 ? (
              <div className="text-center text-t4 py-6 text-sm">Δεν υπάρχουν εκκρεμείς αλλαγές 🎉</div>
            ) : (
              <div className="space-y-2">
                {pending.map(req => (
                  <div key={req.id} className="flex items-center gap-3 bg-surface-0/50 border border-line rounded-lg p-3">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap ${typeTone(req.change_type)}`}>{typeLabel(req.change_type)}</span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-t1 truncate">{req.change_summary}</p>
                      <p className="text-[11px] text-t4 truncate">{req.created_by_name || req.organization_name || ''} · {fmtDate(req.created_at)}</p>
                    </div>
                    <button onClick={() => accept(req.id)} disabled={busy === req.id}
                      className="cashier-primary flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-500 text-white rounded-lg disabled:opacity-50 flex-shrink-0">
                      <Check className="h-4 w-4" /> {busy === req.id ? '…' : 'Αποδοχή'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* History */}
          <div>
            <h4 className="text-sm font-semibold text-t1 flex items-center gap-2 mb-3"><History className="h-4 w-4 text-t3" /> Πρόσφατο ιστορικό</h4>
            {history.length === 0 ? (
              <div className="text-center text-t4 py-4 text-sm">—</div>
            ) : (
              <div className="space-y-1.5">
                {history.map(h => (
                  <div key={h.id} className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-surface-3 dark:hover:bg-surface-2/30">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap ${typeTone(h.change_type)}`}>{typeLabel(h.change_type)}</span>
                    <p className="text-sm text-t2 truncate flex-1 min-w-0">{h.change_summary}</p>
                    <span className="text-[11px] text-t4 whitespace-nowrap">{statusLabel(h.status)} · {fmtDate(h.applied_at || h.reviewed_at || h.created_at)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CatalogChangesModal;
